import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { socketManager } from 'utils/socketManager';
import { profileApi } from 'store/services/profile/profileApi';
import { PointsUpdatePayload } from 'types/interfaces/points';

interface UseProfilePointsSocketOptions {
  /** The user ID of the profile being viewed */
  userId: number | undefined;
  /** The username of the profile being viewed (used as profileApi cache key) */
  username: string;
  /** Whether the subscription is active */
  enabled?: boolean;
}

/**
 * Hook to subscribe to real-time point balance updates for a viewed profile.
 *
 * Emits profile:watch on mount to join the profile-${userId} socket room,
 * listens for profile:points_update events, and directly patches the
 * profileApi cache so ProfileAnimatedPoints animates immediately.
 *
 * Emits profile:unwatch on unmount to leave the room.
 */
export const useProfilePointsSocket = ({
  userId,
  username,
  enabled = true,
}: UseProfilePointsSocketOptions): void => {
  const dispatch = useDispatch();
  const hasAcquiredRef = useRef(false);

  // Stable refs to avoid re-registering socket listeners
  const usernameRef = useRef(username);
  useEffect(() => {
    usernameRef.current = username;
  }, [username]);

  useEffect(() => {
    if (!enabled || !userId) return;

    hasAcquiredRef.current = true;
    const socket = socketManager.acquireSocket();

    // Join the profile room
    socket.emit('profile:watch', { userId });

    const handleProfilePointsUpdate = (payload: PointsUpdatePayload) => {
      console.log('[ProfilePointsSocket] Received profile:points_update', payload);

      // Directly patch the profileApi cache for the viewed username
      (dispatch as any)(
        profileApi.util.updateQueryData(
          'getPublicProfile',
          usernameRef.current,
          (draft: any) => {
            if (draft) {
              draft.points = payload.newBalance;
            }
          }
        )
      );
    };

    socket.on('profile:points_update', handleProfilePointsUpdate);

    return () => {
      socket.emit('profile:unwatch', { userId });
      socket.off('profile:points_update', handleProfilePointsUpdate);

      if (hasAcquiredRef.current) {
        socketManager.releaseSocket();
        hasAcquiredRef.current = false;
      }
    };
  }, [enabled, userId, dispatch]);
};

export default useProfilePointsSocket;
